import React, { useEffect, useMemo, useState } from "react";
import CustomColorPicker from "../CustomColorPicker";
import GradientStopTrack from "./GradientStopTrack";
import {
  buildGradientCss,
  parseGradientCss,
  defaultGradientState,
  interpolateColorAt,
  clampAngle,
  clampPosition,
  GRADIENT_TYPES,
  LINEAR_DIRECTION_PRESETS,
  RADIAL_SHAPES,
  GRADIENT_POSITIONS,
  MAX_GRADIENT_STOPS,
  MIN_GRADIENT_STOPS,
} from "./gradientCss";
import * as S from "./GradientBuilder.styled";

/**
 * Custom Gradient builder.
 *
 * Self-contained: lets a user compose their own gradient (type + direction +
 * color stops) and apply it as a CSS background string. The ONLY integration
 * point is `onApply(cssString)` — the picker already routes that to
 * handleGradientColor -> bg_wallpaper, so this needs no new wiring or contract.
 *
 * @param {(css: string) => void} onApply called with the generated CSS string
 *   when the user applies the gradient (same callback the preset swatches use).
 * @param {string} [value] the currently-applied background string; if it parses
 *   as a gradient this builder produced, the UI seeds from it (edit mode).
 * @param {boolean} [applyOnChange=false] when true, calls onApply on every edit
 *   (live, no Apply button); when false, edits are local until Apply is pressed.
 */
const GradientBuilder = ({ onApply, value, applyOnChange = false }) => {
  // Seed from an existing custom gradient when possible (parse-back), else default.
  const [state, setState] = useState(() => {
    const parsed = parseGradientCss(value);
    return parsed || defaultGradientState();
  });

  // Color-picker modal target: index of the stop being edited, or null.
  const [editingStop, setEditingStop] = useState(null);

  // Currently-selected handle on the draggable track (shows its remove ✕), or
  // null. Distinct from `editingStop`: selecting highlights a handle; clicking
  // it opens the picker (which sets `editingStop`).
  const [selectedStop, setSelectedStop] = useState(null);

  // Opening the color picker for a stop also selects it on the track, so the
  // numeric list, the track handle, and the picker all reference the same stop.
  const editStopColor = (index) => {
    setSelectedStop(index);
    setEditingStop(index);
  };

  // Re-seed if the applied value changes to a different parseable gradient
  // (e.g. user picked a preset, then re-opened the builder). Never clobbers an
  // in-progress edit with an unparseable value.
  useEffect(() => {
    const parsed = parseGradientCss(value);
    if (parsed && value !== buildGradientCss(state)) {
      setState(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const css = useMemo(() => buildGradientCss(state), [state]);

  // Push the generated string upward. In live mode this fires on every change.
  const emit = (next) => {
    if (applyOnChange && typeof onApply === "function") {
      onApply(buildGradientCss(next));
    }
  };

  const update = (patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      emit(next);
      return next;
    });
  };

  const updateStop = (index, patch) => {
    setState((prev) => {
      const stops = prev.stops.map((stop, i) =>
        i === index ? { ...stop, ...patch } : stop,
      );
      const next = { ...prev, stops };
      emit(next);
      return next;
    });
  };

  const addStop = () => {
    setState((prev) => {
      if (prev.stops.length >= MAX_GRADIENT_STOPS) return prev;
      // Insert a stop midway between the last two, picking the last color.
      const last = prev.stops[prev.stops.length - 1];
      const prevStop = prev.stops[prev.stops.length - 2] || { position: 0 };
      let position = clampPosition(
        Math.round((last.position + prevStop.position) / 2),
      );
      // Ensure the new stop's position is unique — nudge ±1 within [0,100]
      // if the midpoint collides with an existing stop.
      const taken = new Set(prev.stops.map((s) => s.position));
      if (taken.has(position)) {
        let nudged = position;
        for (let d = 1; d <= 100; d += 1) {
          if (position + d <= 100 && !taken.has(position + d)) {
            nudged = position + d;
            break;
          }
          if (position - d >= 0 && !taken.has(position - d)) {
            nudged = position - d;
            break;
          }
        }
        position = nudged;
      }
      const stops = [
        ...prev.stops.slice(0, -1),
        { color: last.color, position },
        last,
      ];
      const next = { ...prev, stops };
      emit(next);
      return next;
    });
  };

  // Add a stop at an explicit position % (from clicking the track bar). The
  // color is interpolated from the neighbouring stops so the new stop blends
  // into the gradient at that point; it is inserted in sorted position so the
  // numeric list stays ordered. Respects the MAX cap (no-op when full).
  const addStopAt = (position) => {
    setState((prev) => {
      if (prev.stops.length >= MAX_GRADIENT_STOPS) return prev;
      const pos = clampPosition(position);
      const color = interpolateColorAt(prev.stops, pos);
      const newStop = { color, position: pos };
      // Insert keeping ascending position order.
      const stops = [...prev.stops];
      let insertAt = stops.findIndex((s) => clampPosition(s.position) > pos);
      if (insertAt === -1) insertAt = stops.length;
      stops.splice(insertAt, 0, newStop);
      const next = { ...prev, stops };
      emit(next);
      // Select the freshly added stop so its remove ✕ is reachable.
      setEditingStop(null);
      setSelectedStop(insertAt);
      return next;
    });
  };

  const removeStop = (index) => {
    setState((prev) => {
      if (prev.stops.length <= MIN_GRADIENT_STOPS) return prev;
      const stops = prev.stops.filter((_, i) => i !== index);
      const next = { ...prev, stops };
      emit(next);
      return next;
    });
    // Keep the selection valid after removal.
    setSelectedStop((sel) => {
      if (sel === null) return null;
      if (sel === index) return null;
      return sel > index ? sel - 1 : sel;
    });
  };

  // Reorder a stop one slot left/right (simple, dependency-free reordering).
  const moveStop = (index, dir) => {
    setState((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.stops.length) return prev;
      const stops = [...prev.stops];
      [stops[index], stops[target]] = [stops[target], stops[index]];
      const next = { ...prev, stops };
      emit(next);
      return next;
    });
  };

  const handleApply = () => {
    if (typeof onApply === "function") onApply(css);
  };

  const canAdd = state.stops.length < MAX_GRADIENT_STOPS;
  const canRemove = state.stops.length > MIN_GRADIENT_STOPS;

  return (
    <S.Root>
      {/* Live preview of the composed gradient. */}
      <S.Preview css={css} aria-label="Gradient preview" />

      {/* Gradient type: linear / radial / conic */}
      <div>
        <S.SectionLabel>Type</S.SectionLabel>
        <S.SegmentGroup role="group" aria-label="Gradient type">
          {GRADIENT_TYPES.map((t) => (
            <S.SegmentButton
              key={t}
              type="button"
              active={state.type === t}
              aria-pressed={state.type === t}
              onClick={() => update({ type: t })}
            >
              {t}
            </S.SegmentButton>
          ))}
        </S.SegmentGroup>
      </div>

      {/* Direction controls — differ per type. */}
      {state.type === "linear" && (
        <div>
          <S.SectionLabel>Direction</S.SectionLabel>
          <S.Row>
            <S.SegmentGroup role="group" aria-label="Direction preset">
              {LINEAR_DIRECTION_PRESETS.map((preset) => (
                <S.SegmentButton
                  key={preset.id}
                  type="button"
                  active={state.angle === preset.angle}
                  aria-pressed={state.angle === preset.angle}
                  onClick={() => update({ angle: preset.angle })}
                >
                  {preset.label}
                </S.SegmentButton>
              ))}
            </S.SegmentGroup>
          </S.Row>
          <S.AngleRow style={{ marginTop: 8 }}>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={state.angle}
              aria-label="Custom angle"
              onChange={(e) => update({ angle: clampAngle(e.target.value) })}
              style={{ flex: 1, accentColor: "#81459B" }}
            />
            <S.AngleInput
              type="number"
              min={0}
              max={360}
              value={state.angle}
              aria-label="Angle in degrees"
              onChange={(e) => update({ angle: clampAngle(e.target.value) })}
            />
            <span style={{ fontSize: 12, color: "#6b6b6b" }}>deg</span>
          </S.AngleRow>
        </div>
      )}

      {state.type === "radial" && (
        <div>
          <S.SectionLabel>Shape &amp; Position</S.SectionLabel>
          <S.Row>
            <S.SegmentGroup role="group" aria-label="Radial shape">
              {RADIAL_SHAPES.map((shape) => (
                <S.SegmentButton
                  key={shape}
                  type="button"
                  active={state.shape === shape}
                  aria-pressed={state.shape === shape}
                  onClick={() => update({ shape })}
                >
                  {shape}
                </S.SegmentButton>
              ))}
            </S.SegmentGroup>
            <PositionSelect
              value={state.position}
              onChange={(position) => update({ position })}
            />
          </S.Row>
        </div>
      )}

      {state.type === "conic" && (
        <div>
          <S.SectionLabel>From-angle &amp; Position</S.SectionLabel>
          <S.AngleRow>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={state.fromAngle}
              aria-label="Conic from-angle"
              onChange={(e) =>
                update({ fromAngle: clampAngle(e.target.value) })
              }
              style={{ flex: 1, accentColor: "#81459B" }}
            />
            <S.AngleInput
              type="number"
              min={0}
              max={360}
              value={state.fromAngle}
              aria-label="From-angle in degrees"
              onChange={(e) =>
                update({ fromAngle: clampAngle(e.target.value) })
              }
            />
            <span style={{ fontSize: 12, color: "#6b6b6b" }}>deg</span>
          </S.AngleRow>
          <S.Row style={{ marginTop: 8 }}>
            <PositionSelect
              value={state.position}
              onChange={(position) => update({ position })}
            />
          </S.Row>
        </div>
      )}

      {/* Color stops */}
      <div>
        <S.SectionLabel>
          Color stops ({state.stops.length}/{MAX_GRADIENT_STOPS})
        </S.SectionLabel>

        {/* Draggable stop-track — the intuitive/"pro" layer. Edits the SAME
            stops as the numeric list below (two-way synced): drag a handle and
            the number updates; change a number and the handle moves. */}
        <GradientStopTrack
          stops={state.stops}
          gradientCss={css}
          selectedIndex={selectedStop}
          onSelect={setSelectedStop}
          onEditColor={editStopColor}
          onAddStopAt={addStopAt}
          onMoveStop={(index, position) => {
            setSelectedStop(index);
            updateStop(index, { position });
          }}
          onRemoveStop={removeStop}
          canAdd={canAdd}
          canRemove={canRemove}
        />

        <S.StopList>
          {state.stops.map((stop, index) => (
            <S.StopRow key={index}>
              <S.StopSwatch
                type="button"
                color={stop.color}
                aria-label={`Stop ${index + 1} color ${stop.color}`}
                onClick={() => editStopColor(index)}
              />
              <S.StopPosInput
                type="number"
                min={0}
                max={100}
                value={stop.position}
                aria-label={`Stop ${index + 1} position percent`}
                onFocus={() => setSelectedStop(index)}
                onChange={(e) => {
                  setSelectedStop(index);
                  updateStop(index, {
                    position: clampPosition(e.target.value),
                  });
                }}
              />
              <span style={{ fontSize: 12, color: "#9a9aa0" }}>%</span>
              <S.IconButton
                type="button"
                aria-label={`Move stop ${index + 1} earlier`}
                disabled={index === 0}
                onClick={() => moveStop(index, -1)}
              >
                ‹
              </S.IconButton>
              <S.IconButton
                type="button"
                aria-label={`Move stop ${index + 1} later`}
                disabled={index === state.stops.length - 1}
                onClick={() => moveStop(index, 1)}
              >
                ›
              </S.IconButton>
              <S.IconButton
                type="button"
                aria-label={`Remove stop ${index + 1}`}
                disabled={!canRemove}
                onClick={() => removeStop(index)}
              >
                ×
              </S.IconButton>
            </S.StopRow>
          ))}
        </S.StopList>
        <div style={{ marginTop: 8 }}>
          <S.AddStopButton
            type="button"
            disabled={!canAdd}
            onClick={addStop}
          >
            + Add color stop
          </S.AddStopButton>
          {!canAdd && (
            <S.Hint>Maximum of {MAX_GRADIENT_STOPS} stops reached.</S.Hint>
          )}
        </div>
      </div>

      {!applyOnChange && (
        <S.ApplyButton type="button" onClick={handleApply}>
          Apply gradient
        </S.ApplyButton>
      )}

      {/* Reused color picker modal, opened per-stop. */}
      {editingStop !== null && state.stops[editingStop] && (
        <CustomColorPicker
          visible={editingStop !== null}
          color={state.stops[editingStop].color}
          onColorChange={(color) => updateStop(editingStop, { color })}
          onClose={() => setEditingStop(null)}
          onConfirm={() => setEditingStop(null)}
        />
      )}
    </S.Root>
  );
};

/** Native select for the CSS named positions (radial + conic). */
const PositionSelect = ({ value, onChange }) => (
  <select
    value={value}
    aria-label="Position"
    onChange={(e) => onChange(e.target.value)}
    style={{
      fontFamily: '"DM Sans", sans-serif',
      fontSize: 12,
      padding: "6px 8px",
      border: "1px solid #e1e4e8",
      borderRadius: 8,
      background: "#fff",
      cursor: "pointer",
    }}
  >
    {GRADIENT_POSITIONS.map((p) => (
      <option key={p.id} value={p.id}>
        {p.label}
      </option>
    ))}
  </select>
);

export default GradientBuilder;
